import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Zap, Users, ArrowLeft, ArrowRight, Loader2, Plus, X, CheckCircle, Mail,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_OPTIONS = [
  "Newsletter", "Twitter / X", "LinkedIn", "YouTube", "Podcast",
  "Blog", "TikTok", "Instagram", "Reddit", "Forum / Community",
];

export default function PromoterApply() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", audience_description: "",
    primary_channels: [], sample_links: ["", ""], monthly_reach: "", pitch: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const toggleChannel = (c) => {
    setForm((f) => ({
      ...f,
      primary_channels: f.primary_channels.includes(c)
        ? f.primary_channels.filter((x) => x !== c)
        : [...f.primary_channels, c],
    }));
  };

  const addLink = () => setForm((f) => ({ ...f, sample_links: [...f.sample_links, ""] }));
  const removeLink = (i) =>
    setForm((f) => ({ ...f, sample_links: f.sample_links.filter((_, idx) => idx !== i) }));
  const setLink = (i, v) =>
    setForm((f) => {
      const next = [...f.sample_links];
      next[i] = v;
      return { ...f, sample_links: next };
    });

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!form.name.trim()) { setError("Please add your name"); return; }
    if (!form.email.includes("@")) { setError("Please add a valid email"); return; }
    if (form.audience_description.trim().length < 30) { setError("Please describe your audience in at least 30 characters"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/promoter/apply`, {
        ...form,
        sample_links: form.sample_links.filter((l) => l.trim().length > 0),
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50" data-testid="promoter-apply-page">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 pt-32 pb-20 text-center" data-testid="promoter-apply-success">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center mb-5">
            <CheckCircle size={28} className="text-emerald-700" />
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-950 mb-2">{t("promoter_apply.success")}</h1>
          <p className="text-sm text-zinc-600">{t("promoter_apply.success_msg")}</p>
          <p className="text-xs text-zinc-400 mt-4">Application ID: {success.application_id}</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-950 font-semibold"><ArrowLeft size={14} /> Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="promoter-apply-page">
      <Navbar />
      <section className="max-w-2xl mx-auto px-4 pt-32 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-zinc-200 text-zinc-700 text-xs font-semibold mb-5">
          <Users size={12} /> Promoter program
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-950 leading-[1.05]">
          {t("promoter_apply.headline")}
        </h1>
        <p className="text-lg text-zinc-600 mt-5 max-w-xl leading-relaxed">{t("promoter_apply.subhead")}</p>

        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-3xl p-6 mt-10 space-y-5" data-testid="promoter-apply-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.name")} *</label>
              <input data-testid="input-apply-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.email")} *</label>
              <input data-testid="input-apply-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.audience")} *</label>
            <p className="text-xs text-zinc-500 mb-2">{t("promoter_apply.audience_help")}</p>
            <textarea data-testid="input-apply-audience" required rows={4} value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })}
              className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
            <div className="text-[11px] text-zinc-400 mt-1 text-right">{form.audience_description.length}/2000</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-2">{t("promoter_apply.channels")}</label>
            <div className="flex flex-wrap gap-2" data-testid="apply-channels">
              {CHANNEL_OPTIONS.map((c) => {
                const selected = form.primary_channels.includes(c);
                return (
                  <button type="button" key={c} onClick={() => toggleChannel(c)}
                    data-testid={`channel-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${selected ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.samples")}</label>
            <p className="text-xs text-zinc-500 mb-2">{t("promoter_apply.samples_help")}</p>
            <div className="space-y-2" data-testid="apply-samples">
              {form.sample_links.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`input-apply-sample-${i}`} type="url" value={l} onChange={(e) => setLink(i, e.target.value)}
                    placeholder="https://example.com/your-best-piece"
                    className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                  {form.sample_links.length > 1 && (
                    <button type="button" data-testid={`btn-remove-sample-${i}`} onClick={() => removeLink(i)} className="p-2 text-zinc-400 hover:bg-zinc-100 rounded-lg"><X size={14} /></button>
                  )}
                </div>
              ))}
              {form.sample_links.length < 10 && (
                <button type="button" data-testid="btn-add-sample" onClick={addLink} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-950"><Plus size={11} /> Add another</button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.reach")}</label>
            <input data-testid="input-apply-reach" value={form.monthly_reach} onChange={(e) => setForm({ ...form, monthly_reach: e.target.value })}
              placeholder="e.g. ~25k newsletter subscribers + 5k Twitter"
              className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-600 mb-1">{t("promoter_apply.pitch")}</label>
            <p className="text-xs text-zinc-500 mb-2">{t("promoter_apply.pitch_help")}</p>
            <textarea data-testid="input-apply-pitch" rows={3} value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })}
              placeholder="e.g. A weekly issue covering AI tools for solo founders, with a 3-min spotlight on FastPrototype.ai as my recommended MVP shop."
              className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
          </div>

          {error && <div data-testid="apply-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          <button type="submit" disabled={submitting} data-testid="btn-submit-apply"
            className="w-full py-3 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> {t("promoter_apply.submitting")}</> : <>{t("promoter_apply.submit")} <ArrowRight size={14} /></>}
          </button>

          <p className="text-[11px] text-zinc-400 text-center">
            By applying you agree to be contacted at the email provided. Approval is at FastPrototype.ai's sole discretion.
            Already a promoter? <Link to="/promoter/login" className="underline">Sign in</Link>.
          </p>
        </form>
      </section>
      <Footer />
    </div>
  );
}
